import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { GET_CUSTOMER_DATA, GET_CUSTOMER_ORDERS, REORDER_ITEMS } from '../queries/customerQueries';
import AccountNavigation from './AccountNavigation';
import Loader from './Loader';
import { Link } from 'react-router-dom';
import './MyAccount.css';

const AccountOrders = () => {
  const { user, logout, isInitializing } = useAuth();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [reorderLoading, setReorderLoading] = useState('');
  const [reorderError, setReorderError] = useState('');
  const [reorderSuccess, setReorderSuccess] = useState('');

  const { data: customerData } = useQuery(GET_CUSTOMER_DATA);
  const { data: ordersData, loading: ordersLoading, error: ordersError } = useQuery(GET_CUSTOMER_ORDERS, {
    variables: { pageSize: 10, currentPage: 1 }
  });
  const [reorderItems] = useMutation(REORDER_ITEMS);

  if (isInitializing) {
    return (
      <div className="my-account-container">
        <Loader fullScreen={true} size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-account-container">
        <div className="my-account-error">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
        </div>
      </div>
    );
  }

  const handleReorder = async (orderNumber) => {
    try {
      setReorderLoading(orderNumber);
      setReorderError('');
      setReorderSuccess('');

      const result = await reorderItems({
        variables: { orderNumber: orderNumber }
      });

      const reorderResult = result.data?.reorderItems;

      if (reorderResult?.userInputErrors && reorderResult.userInputErrors.length > 0) {
        const errorMessages = reorderResult.userInputErrors.map(error => error.message).join(', ');
        throw new Error(errorMessages);
      }

      if (reorderResult?.cart?.id) {
        setReorderSuccess('Items successfully added to your cart!');
        setTimeout(() => setReorderSuccess(''), 5000);
      } else {
        throw new Error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error reordering:', error);
      setReorderError(error.message || 'Failed to reorder items. Some items may no longer be available.');
      setTimeout(() => setReorderError(''), 8000);
    } finally {
      setReorderLoading('');
    }
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };

  const closeOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const renderOrderDetailsModal = () => {
    if (!isOrderModalOpen || !selectedOrder) return null;

    return (
      <div className="modal-overlay" onClick={closeOrderModal}>
        <div className="order-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Order Details - #{selectedOrder.number}</h2>
            <button className="modal-close" onClick={closeOrderModal}>×</button>
          </div>

          <div className="modal-content">
            <div className="order-summary">
              <div className="order-info-grid">
                <div className="info-item">
                  <label>Order Date</label>
                  <span>{new Date(selectedOrder.order_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <span className={`status ${selectedOrder.status.toLowerCase()}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div className="info-item">
                  <label>Total</label>
                  <span className="total-amount">
                    {selectedOrder.total?.grand_total?.value} {selectedOrder.total?.grand_total?.currency}
                  </span>
                </div>
              </div>
            </div>

            {selectedOrder.items && selectedOrder.items.length > 0 && (
              <div className="order-items-section">
                <h3>Items Ordered</h3>
                <div className="order-items">
                  {selectedOrder.items.map((item, index) => (
                    <div key={item.id || index} className="order-item">
                      <div className="item-details">
                        <h4>{item.product_name}</h4>
                        <p>SKU: {item.product_sku}</p>
                        <p>Quantity: {item.quantity_ordered}</p>
                      </div>
                      <div className="item-price">
                        {item.product_sale_price?.value} {item.product_sale_price?.currency}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedOrder.shipping_address && (
              <div className="address-section">
                <h3>Shipping Address</h3>
                <div className="address-details">
                  <p><strong>{selectedOrder.shipping_address.firstname} {selectedOrder.shipping_address.lastname}</strong></p>
                  <p>{selectedOrder.shipping_address.street?.join(', ')}</p>
                  <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.region} {selectedOrder.shipping_address.postcode}</p>
                  <p>{selectedOrder.shipping_address.country_code}</p>
                  {selectedOrder.shipping_address.telephone && <p>Phone: {selectedOrder.shipping_address.telephone}</p>}
                </div>
              </div>
            )}

            {selectedOrder.billing_address && (
              <div className="address-section">
                <h3>Billing Address</h3>
                <div className="address-details">
                  <p><strong>{selectedOrder.billing_address.firstname} {selectedOrder.billing_address.lastname}</strong></p>
                  <p>{selectedOrder.billing_address.street?.join(', ')}</p>
                  <p>{selectedOrder.billing_address.city}, {selectedOrder.billing_address.region} {selectedOrder.billing_address.postcode}</p>
                  <p>{selectedOrder.billing_address.country_code}</p>
                  {selectedOrder.billing_address.telephone && <p>Phone: {selectedOrder.billing_address.telephone}</p>}
                </div>
              </div>
            )}

            {selectedOrder.payment_methods && selectedOrder.payment_methods.length > 0 && (
              <div className="payment-section">
                <h3>Payment Method</h3>
                <div className="payment-details">
                  {selectedOrder.payment_methods.map((payment, index) => (
                    <p key={index}><strong>{payment.name}</strong> ({payment.type})</p>
                  ))}
                </div>
              </div>
            )}

            {selectedOrder.total && (
              <div className="totals-section">
                <h3>Order Totals</h3>
                <div className="totals-grid">
                  {selectedOrder.total.subtotal && (
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>{selectedOrder.total.subtotal.value} {selectedOrder.total.subtotal.currency}</span>
                    </div>
                  )}
                  {selectedOrder.total.total_shipping && (
                    <div className="total-row">
                      <span>Shipping:</span>
                      <span>{selectedOrder.total.total_shipping.value} {selectedOrder.total.total_shipping.currency}</span>
                    </div>
                  )}
                  {selectedOrder.total.total_tax && (
                    <div className="total-row">
                      <span>Tax:</span>
                      <span>{selectedOrder.total.total_tax.value} {selectedOrder.total.total_tax.currency}</span>
                    </div>
                  )}
                  <div className="total-row grand-total">
                    <span>Grand Total:</span>
                    <span>{selectedOrder.total.grand_total.value} {selectedOrder.total.grand_total.currency}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="my-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        {customerData?.customer?.firstname ? (
          <p>Welcome back, {customerData.customer.firstname}!</p>
        ) : ('')}
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="account-content">
        <AccountNavigation />
        <main className="account-main">
          <div className="account-section">
            {reorderSuccess && <div className="success-message">{reorderSuccess}</div>}
            {reorderError && <div className="error-message">{reorderError}</div>}
            {ordersLoading ? (
              <div className="loading">Loading orders...</div>
            ) : ordersError ? (
              <div className="error">Error loading orders: {ordersError.message}</div>
            ) : (
              <div className="orders-list">
                {ordersData?.customer?.orders?.items?.length > 0 ? (
                  ordersData.customer.orders.items.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div className="order-info">
                          <h4>Order #{order.number}</h4>
                          <p className="order-date">
                            Placed on {new Date(order.order_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="order-status">
                          <span className={`status ${order.status.toLowerCase()}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                      <div className="order-details">
                        <div className="order-total">
                          <strong>
                            {order.total?.grand_total?.value} {order.total?.grand_total?.currency}
                          </strong>
                        </div>
                        <button
                          className="btn-secondary"
                          onClick={() => openOrderDetails(order)}
                        >
                          View Details
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleReorder(order.number)}
                          disabled={reorderLoading === order.number}
                        >
                          {reorderLoading === order.number ? 'Reordering...' : 'Reorder'}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No orders found</p>
                    <Link to="/" className="btn-primary">Start Shopping</Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {renderOrderDetailsModal()}
    </div>
  );
};

export default AccountOrders;